/**
 * --------------------------------------------------
 * STORE-SPECIFIC JAVASCRIPT
 * --------------------------------------------------
 * 
 * This file is reserved for store-specific scripts.
 */


(function () {
  const WISER_ATC_SELECTORS = [
    '.ws_atc_cart_text',
    '.ws_Product_Button',
  ];
  const WISER_CARD_SELECTORS = [
    '.ws_card_wrapper',
    '.ws_cart_card_wrapper',
    '.ws_product_card',
    '.ws_product_item',
    '.ws_card',
    '.ws-product-card',
  ];
  const WISER_DEFAULT_ERROR_MESSAGE = "That's all we have in stock.";

  let refreshTimer = null;
  let refreshInFlight = false;
  let wiserThemeAddInFlight = false;
  let pendingWiserAddToCart = null;
  let pendingWiserTimer = null;

  function getCartUrl() {
    if (window.routes && window.routes.cart_url) {
      return window.routes.cart_url;
    }

    return '/cart';
  }

  function updateHeaderCartCount(cartData) {
    const count = cartData && typeof cartData.item_count === 'number' ? cartData.item_count : 0;
    const countBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');

    if (count === 0) {
      countBubble?.remove();
      document.getElementById('cart-icon-bubble')?.setAttribute('aria-label', 'Cart, 0');
      return;
    }

    if (countBubble) {
      countBubble.innerHTML = `<span>${count}</span>`;
      countBubble.setAttribute('aria-hidden', 'true');
      document.getElementById('cart-icon-bubble')?.setAttribute('aria-label', `Cart, ${count}`);
      return;
    }

    const cartIconButton = document.getElementById('cart-icon-bubble');
    if (cartIconButton && count > 0) {
      const bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.setAttribute('aria-hidden', 'true');
      bubble.innerHTML = `<span>${count}</span>`;
      cartIconButton.setAttribute('aria-label', `Cart, ${count}`);
      cartIconButton.appendChild(bubble);
    }
  }

  function syncCartDrawerOverlay(cartDrawer) {
    const overlay = cartDrawer && cartDrawer.querySelector('#CartDrawer-Overlay');
    if (!overlay || typeof cartDrawer.close !== 'function') return;

    overlay.onclick = cartDrawer.close.bind(cartDrawer);
  }

  // Wiser fires wiser:cartUpdated more than once per add; an identical redraw would
  // replace the focused Close button and make screen readers read the drawer again.
  let lastDrawerHtml = null;
  let lastDrawerHtmlAt = 0;

  function replaceCartDrawer(sectionHtml) {
    if (sectionHtml === lastDrawerHtml && Date.now() - lastDrawerHtmlAt < 3000) return;
    lastDrawerHtml = sectionHtml;
    lastDrawerHtmlAt = Date.now();
    const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newDrawer = parsedDocument.querySelector('cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');

    if (!newDrawer || !currentDrawer) return;

    const wasOpen = currentDrawer.classList.contains('active');
    const activeControl = document.activeElement;
    const preservedControl =
      activeControl && currentDrawer.contains(activeControl)
        ? {
            name: activeControl.getAttribute('name'),
            variantId:
              activeControl.dataset.variantId ||
              activeControl.closest('quantity-input')?.querySelector('[data-quantity-variant-id]')?.dataset
                .quantityVariantId,
            remove: activeControl.classList.contains('cart-remove-button'),
          }
        : null;
    currentDrawer.className = newDrawer.className;
    currentDrawer.innerHTML = newDrawer.innerHTML;
    syncCartDrawerOverlay(currentDrawer);

    if (wasOpen) {
      currentDrawer.classList.add('active');
      document.body.classList.add('overflow-hidden');
      let focusTarget = null;
      if (preservedControl?.variantId && preservedControl.name) {
        const input = currentDrawer.querySelector(
          `input[data-quantity-variant-id="${preservedControl.variantId}"]`
        );
        focusTarget = input?.closest('quantity-input')?.querySelector(`button[name="${preservedControl.name}"]`);
      } else if (preservedControl?.remove && typeof window.resolveDistaCartFocus === 'function') {
        focusTarget = window.resolveDistaCartFocus();
      }
      if (!focusTarget && typeof window.resolveDistaCartFocus === 'function') {
        focusTarget = window.resolveDistaCartFocus();
      }
      const dialog = currentDrawer.querySelector('.drawer__inner') || currentDrawer;
      if (focusTarget && dialog.contains(focusTarget)) {
        trapFocus(dialog, focusTarget);
      } else if (typeof currentDrawer.trapDrawerFocus === 'function') {
        currentDrawer.trapDrawerFocus();
      }
    }

    window.announceCartUpdated?.();
  }

  async function refreshCartDrawer() {
    if (refreshInFlight) return;

    refreshInFlight = true;

    try {
      const [drawerResponse, cartResponse] = await Promise.all([
        fetch(`${getCartUrl()}?section_id=cart-drawer`, { cache: 'no-store' }),
        fetch('/cart.js', { cache: 'no-store' }),
      ]);

      if (drawerResponse.ok) {
        const drawerHtml = await drawerResponse.text();
        replaceCartDrawer(drawerHtml);
      }

      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        updateHeaderCartCount(cartData);
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && !cartDrawer.classList.contains('active') && typeof cartDrawer.open === 'function') {
        cartDrawer.open(document.activeElement);
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    } finally {
      refreshInFlight = false;
    }
  }

  function scheduleCartRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCartDrawer, 100);
  }

  function isCartAddUrl(url) {
    if (!url) return false;

    try {
      const parsedUrl = new URL(url, window.location.origin);
      const cartAddPath = window.routes?.cart_add_url || '/cart/add';

      return (
        parsedUrl.pathname === cartAddPath ||
        parsedUrl.pathname === `${cartAddPath}.js` ||
        parsedUrl.pathname.endsWith('/cart/add') ||
        parsedUrl.pathname.endsWith('/cart/add.js')
      );
    } catch (error) {
      return String(url).includes('/cart/add');
    }
  }

  function setPendingWiserAddToCart(button) {
    pendingWiserAddToCart = {
      button,
      createdAt: Date.now(),
    };

    clearTimeout(pendingWiserTimer);
    pendingWiserTimer = setTimeout(clearPendingWiserAddToCart, 8000);
  }

  function clearPendingWiserAddToCart() {
    pendingWiserAddToCart = null;
    clearTimeout(pendingWiserTimer);
    pendingWiserTimer = null;
  }

  function getRecentPendingWiserButton() {
    if (!pendingWiserAddToCart || Date.now() - pendingWiserAddToCart.createdAt > 8000) {
      clearPendingWiserAddToCart();
      return null;
    }

    return pendingWiserAddToCart.button;
  }

  function getWiserCard(button) {
    return button?.closest(WISER_CARD_SELECTORS.join(',')) || button?.closest('[class*="ws_"]') || button?.parentElement;
  }

  function clearWiserCartError(button) {
    const card = getWiserCard(button);
    card?.querySelector('.wiser-cart-error-message')?.remove();
  }

  function getCartErrorMessage(responseData) {
    if (!responseData) return WISER_DEFAULT_ERROR_MESSAGE;

    if (typeof responseData === 'string') return responseData;
    if (Array.isArray(responseData)) return responseData.join(' ');

    if (responseData.errors && typeof responseData.errors === 'object') {
      return Object.values(responseData.errors).flat().join(' ') || WISER_DEFAULT_ERROR_MESSAGE;
    }

    return (
      responseData.description ||
      responseData.message ||
      responseData.errors ||
      WISER_DEFAULT_ERROR_MESSAGE
    );
  }

  function findWiserVariantId(button) {
    const form = button.closest('form');
    const card = getWiserCard(button);
    const scope = form || card;
    const directValue =
      button.dataset.variantId ||
      button.dataset.variant ||
      button.dataset.id ||
      button.getAttribute('variant-id') ||
      button.getAttribute('data-var-id');

    if (directValue) return directValue;

    const variantField = scope?.querySelector(
      [
        'input[name="id"]',
        'select[name="id"]',
        'input[name="variant_id"]',
        'select[name="variant_id"]',
        '[data-variant-id]',
        '[variant-id]',
      ].join(',')
    );

    return (
      variantField?.value ||
      variantField?.dataset?.variantId ||
      variantField?.getAttribute('variant-id') ||
      null
    );
  }

  function getWiserQuantity(button) {
    const scope = button.closest('form') || getWiserCard(button);
    const quantityInput = scope?.querySelector('input[name="quantity"], input[type="number"]');
    const quantity = parseInt(quantityInput?.value, 10);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function setWiserButtonLoading(button, isLoading) {
    if (!button) return;

    if (isLoading) {
      button.dataset.wiserOriginalText = button.textContent;
      // No aria-disabled: NVDA reads it as "unavailable"; wiserThemeAddInFlight blocks double adds.
      button.classList.add('loading');
      return;
    }

    button.classList.remove('loading');
  }

  async function addWiserProductWithThemePipeline(button) {
    const variantId = findWiserVariantId(button);
    if (!variantId || wiserThemeAddInFlight) return false;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', getWiserQuantity(button));

    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(button);
    }

    const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
      },
    };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    wiserThemeAddInFlight = true;
    window.distaThemeCartChangeAt = Date.now();
    setWiserButtonLoading(button, true);

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
      const responseData = await response.json();

      if (responseData.status) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'wiser-product-card',
            productVariantId: formData.get('id'),
            errors: responseData.errors || responseData.description,
            message: responseData.message,
          });
        }
        showWiserCartError(button, responseData);
        return true;
      }

      if (cart && typeof cart.renderContents === 'function' && responseData.sections) {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'wiser-product-card',
            productVariantId: formData.get('id'),
            cartData: responseData,
          });
        }
        cart.renderContents(responseData);
        if (window.DistaA11y && window.distaA11yStrings?.addedToCart && responseData.product_title) {
          window.DistaA11y.announce(window.distaA11yStrings.addedToCart.replace('__PRODUCT__', responseData.product_title));
        }
      } else {
        scheduleCartRefresh();
      }

      return true;
    } catch (error) {
      console.error('Wiser add to cart failed:', error);
      showWiserCartError(button, WISER_DEFAULT_ERROR_MESSAGE);
      return true;
    } finally {
      wiserThemeAddInFlight = false;
      setWiserButtonLoading(button, false);
      clearPendingWiserAddToCart();
    }
  }

  function showWiserCartError(button, message) {
    const card = getWiserCard(button);
    if (!card) return;

    clearWiserCartError(button);

    const errorMessage = document.createElement('div');
    errorMessage.className = 'wiser-cart-error-message';
    errorMessage.setAttribute('role', 'alert');

    const iconWrapper = document.createElement('span');
    iconWrapper.className = 'svg-wrapper';
    iconWrapper.innerHTML = `
      <svg class="icon icon-error" style="height: 28px" viewBox="0 0 13 13" aria-hidden="true">
        <circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="2"></circle>
        <circle cx="6.5" cy="6.5" r="5.5" fill="#cc0c39" stroke="#cc0c39" stroke-width=".7"></circle>
        <path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path>
        <path fill="#fff" stroke="#cc0c39" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path>
      </svg>
    `;
    errorMessage.appendChild(iconWrapper);
    errorMessage.appendChild(document.createTextNode(getCartErrorMessage(message)));

    const actionContainer =
      button.closest('.ws_card_info, .ws_cart_card_action, .ws_Product_Addtocart, .ws_product_action') ||
      button.parentElement ||
      card;

    actionContainer.insertAdjacentElement('afterend', errorMessage);

    setTimeout(() => {
      errorMessage.remove();
    }, 6000);
  }

  async function handleWiserFetchCartAddResponse(response) {
    const button = getRecentPendingWiserButton();
    if (!button) return;

    if (response.ok) {
      clearPendingWiserAddToCart();
      scheduleCartRefresh();
      return;
    }

    if (response.status !== 422) return;

    let responseData = "Maximum quantity already added to your cart";

    showWiserCartError(button, responseData);
    clearPendingWiserAddToCart();
  }

  function handleWiserXhrCartAddResponse(xhr) {
    const button = getRecentPendingWiserButton();
    if (!button) return;

    if (xhr.status >= 200 && xhr.status < 300) {
      clearPendingWiserAddToCart();
      scheduleCartRefresh();
      return;
    }

    if (xhr.status !== 422) return;

    let responseData = "Maximum quantity already added to your cart";

    showWiserCartError(button, responseData);
    clearPendingWiserAddToCart();
  }

  function installWiserCartAddInterceptors() {
    if (!window.fetch?.__wiserCartErrorPatched) {
      const originalFetch = window.fetch;
      const patchedFetch = function (...args) {
        const request = args[0];
        const url = typeof request === 'string' ? request : request?.url;
        const fetchPromise = originalFetch.apply(this, args);

        if (!wiserThemeAddInFlight && isCartAddUrl(url)) {
          fetchPromise
            .then((response) => handleWiserFetchCartAddResponse(response))
            .catch(() => {});
        }

        return fetchPromise;
      };

      patchedFetch.__wiserCartErrorPatched = true;
      window.fetch = patchedFetch;
    }

    if (!XMLHttpRequest.prototype.open.__wiserCartErrorPatched) {
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url, ...args) {
        this.__wiserCartAddRequest = isCartAddUrl(url);
        return originalOpen.call(this, method, url, ...args);
      };

      XMLHttpRequest.prototype.send = function (...args) {
        if (this.__wiserCartAddRequest) {
          this.addEventListener('loadend', () => handleWiserXhrCartAddResponse(this));
        }

        return originalSend.apply(this, args);
      };

      XMLHttpRequest.prototype.open.__wiserCartErrorPatched = true;
    }
  }

  installWiserCartAddInterceptors();

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    const wiserAddToCart = event.target.closest(WISER_ATC_SELECTORS.join(','));
    if (!wiserAddToCart) return;

    clearWiserCartError(wiserAddToCart);
    setPendingWiserAddToCart(wiserAddToCart);
    // Wiser's label is "Add <product> to cart"; keep the name for the "added to cart" announcement.
    const wiserLabel = (wiserAddToCart.getAttribute('aria-label') || '').match(/^Add (.+) to cart$/i);
    window.distaWiserAdd = wiserLabel ? { name: wiserLabel[1], at: Date.now() } : null;

    if (findWiserVariantId(wiserAddToCart)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      addWiserProductWithThemePipeline(wiserAddToCart);
    }
  }, true);

  window.addEventListener('wiser:cartUpdated', function () {
    // Wiser fires this for every cart add/change, including the theme's own. The theme already
    // rendered the drawer then; rendering it again re-reads it to screen readers.
    if (window.distaThemeCartChangeAt && Date.now() - window.distaThemeCartChangeAt < 5000) return;
    const added = window.distaWiserAdd;
    if (added && Date.now() - added.at < 8000 && window.DistaA11y && window.distaA11yStrings?.addedToCart) {
      window.distaWiserAdd = null;
      window.DistaA11y.announce(window.distaA11yStrings.addedToCart.replace('__PRODUCT__', added.name));
    }
    clearTimeout(refreshTimer);
    refreshCartDrawer();
  });
})();

// Spark (Hello AI) chat widget custom styling.
// The widget is loaded lazily (on first interaction), so its host element and
// shadowRoot may not exist yet when this file runs. Wait for it, guard against
// null, and apply the style once — avoids the previous null-dereference crash.
(function () {
  var attempts = 0;
  var maxAttempts = 60; // ~30s at 500ms, then give up quietly

  function styleSparkWidget() {
    var host = document.querySelector('.spark-acw-container');

    if (!host || !host.shadowRoot) {
      if (attempts++ < maxAttempts) {
        setTimeout(styleSparkWidget, 500);
      }
      return;
    }

    // Don't inject twice if this runs again.
    if (host.shadowRoot.querySelector('style[data-spark-custom]')) return;

    var style = document.createElement('style');
    style.setAttribute('data-spark-custom', '');
    style.textContent = `
.acw-launcher{
  box-shadow:none !important;
}

.acw-launcher-fab{
  width:50px !important;
  height:50px !important;
}

.acw-panel{
    max-height: 85vh !important;
    min-height: unset !important;
}

.acw-customer-form{
    scrollbar-gutter: unset !important;
}

`;

    host.shadowRoot.appendChild(style);
  }

  styleSparkWidget();
})();

(function () {
  var klaviyoSelector = '[class*="klaviyo"], [id*="klaviyo"]';
  var headingNormalizationScheduled = false;

  function labelIframe(iframe) {
    var iframeId = (iframe.id || '').toLowerCase();
    var iframeSource = (iframe.src || '').toLowerCase();

    if (iframeId === 'preview-bar-iframe') {
      iframe.setAttribute('title', 'Shopify preview toolbar');
    } else if (
      iframeSource.includes('shopify') ||
      iframeSource.includes('web-pixel')
    ) {
      iframe.setAttribute('title', 'Shopify embedded content');
    } else {
      iframe.setAttribute('title', 'Embedded content');
    }
  }

  function labelJudgeMeBadges(root) {
    if (root.matches && root.matches('.jdgm-verified-badge__image img:not([alt])')) {
      root.setAttribute('alt', 'Judge.me verified badge');
    }

    root
      .querySelectorAll('.jdgm-verified-badge__image img:not([alt])')
      .forEach(function (image) {
        image.setAttribute('alt', 'Judge.me verified badge');
      });
  }

  function forEachMatching(root, selector, callback) {
    if (!root || (root.nodeType !== Node.ELEMENT_NODE && root !== document && root.nodeType !== Node.DOCUMENT_NODE)) {
      return;
    }

    if (root.matches && root.matches(selector)) callback(root);
    if (root.querySelectorAll) {
      root.querySelectorAll(selector).forEach(callback);
    }
  }

  function fixNestedInteractive(root) {
    forEachMatching(
      root,
      '.jdgm-rev-widg__summary-stars[role="img"], .jdgm-rev-widg__summary-stars[aria-label]',
      function (stars) {
        var parentControl = stars.closest('a, button, [role="button"], [role="link"]');
        if (!parentControl) return;

        var label = stars.getAttribute('aria-label');
        if (label && !parentControl.getAttribute('aria-label')) {
          parentControl.setAttribute('aria-label', label);
        }

        stars.removeAttribute('role');
        stars.removeAttribute('aria-label');
        stars.setAttribute('aria-hidden', 'true');
      }
    );

    forEachMatching(root, 'a a, button a, a button, button button', function (nested) {
      var wrapper = document.createElement('span');
      Array.from(nested.attributes).forEach(function (attr) {
        if (
          attr.name === 'href' ||
          attr.name === 'role' ||
          attr.name === 'tabindex' ||
          attr.name === 'target' ||
          attr.name.indexOf('aria-') === 0
        ) {
          return;
        }
        wrapper.setAttribute(attr.name, attr.value);
      });
      while (nested.firstChild) wrapper.appendChild(nested.firstChild);
      nested.replaceWith(wrapper);
    });
  }

  function fixProhibitedAria(root) {
    forEachMatching(root, '.star-rating-wrapper[aria-label]', function (el) {
      if (!el.getAttribute('role')) el.setAttribute('role', 'img');
    });

    forEachMatching(root, 'section[aria-label], section[aria-labelledby]', function (el) {
      if (!el.getAttribute('role')) el.setAttribute('role', 'region');
    });
  }

  function removeEmptyHeadings(root) {
    forEachMatching(root, 'h1, h2, h3, h4, h5, h6', function (heading) {
      if (heading.classList.contains('header__heading')) return;
      var text = (heading.textContent || '')
        .replace(/[\s\u00a0\u200b\u200c\u200d\ufeff]+/g, '')
        .trim();
      var hasMedia = heading.querySelector('img, svg, canvas, video');
      if (text === '' && !hasMedia) heading.remove();
    });
  }

  function fixContentinfoLandmarks() {
    document.querySelectorAll('[role="contentinfo"]').forEach(function (el) {
      var insideLandmark = el.parentElement && el.parentElement.closest(
        'footer, header, main, nav, aside, [role="contentinfo"], [role="banner"], [role="main"], [role="navigation"], [role="complementary"]'
      );
      if (insideLandmark || el.tagName !== 'FOOTER') {
        el.removeAttribute('role');
      }
    });
  }

  function fixCardHeadingLevels() {
    document.querySelectorAll('h3.card__heading, h4.card__heading, h5.card__heading, h6.card__heading').forEach(function (heading) {
      var replacement = document.createElement('h2');
      Array.from(heading.attributes).forEach(function (attribute) {
        replacement.setAttribute(attribute.name, attribute.value);
      });
      while (heading.firstChild) replacement.appendChild(heading.firstChild);
      heading.replaceWith(replacement);
    });
  }

  function recurpayOptionInput(label) {
    var input = label.querySelector('input[type="radio"], input[type="checkbox"]');
    if (!input && label.htmlFor) input = document.getElementById(label.htmlFor);
    if (!input) {
      var group = label.closest('.recurpay__group') || label.parentElement;
      if (group) input = group.querySelector('input[type="radio"], input[type="checkbox"]');
    }
    return input;
  }

  function focusRecurpayLabel(label) {
    if (!label || document.activeElement === label) return;
    HTMLElement.prototype.focus.call(label, { preventScroll: true });
  }

  function selectRecurpayOption(label) {
    var input = recurpayOptionInput(label);
    if (input && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    focusRecurpayLabel(label);
  }

  function fixRecurpayPurchaseOptions(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var labels = [];

    if (scope.matches && scope.matches('.recurpay__group_label')) labels.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll('.recurpay__group_label').forEach(function (label) {
        labels.push(label);
      });
    }

    if (!window.__recurpayLabelFocusGuard) {
      window.__recurpayLabelFocusGuard = true;
      document.addEventListener('focusin', function (event) {
        var target = event.target;
        if (!target || !target.matches || !target.matches('input[type="radio"], input[type="checkbox"]')) return;
        var label = target.closest('.recurpay__group_label');
        if (!label && target.id) {
          label = document.querySelector('.recurpay__group_label[for="' + CSS.escape(target.id) + '"]');
        }
        if (!label) {
          var group = target.closest('.recurpay__group');
          if (group) label = group.querySelector('.recurpay__group_label');
        }
        if (!label) return;
        focusRecurpayLabel(label);
      }, true);
    }

    labels.forEach(function (label) {
      label.tabIndex = 0;
      label.removeAttribute('aria-hidden');

      var input = recurpayOptionInput(label);
      if (input) input.tabIndex = -1;

      if (label.dataset.recurpayKeybound) return;
      label.dataset.recurpayKeybound = 'true';

      // The subscription label wraps the frequency <select>; let its own
      // mouse/keyboard events through or the dropdown can't be opened.
      function fromInnerControl(event) {
        var target = event.target;
        if (target && target.nodeType === 3) target = target.parentElement;
        if (!target || typeof target.closest !== 'function') return false;

        var control = target.closest('select, button, a, textarea, input:not([type="radio"]):not([type="checkbox"])');
        if (!control) return false;

        return control !== label && label.contains(control);
      }

      label.addEventListener('mousedown', function (event) {
        if (fromInnerControl(event)) return;
        // event.preventDefault();
        focusRecurpayLabel(label);
      });

      label.addEventListener('keydown', function (event) {
        if (fromInnerControl(event)) return;
        if (event.target !== label) return;
        if (event.code !== 'Enter' && event.code !== 'Space') return;
        // Only Space has a default here (page scroll); nothing else is blocked.
        if (event.code === 'Space') event.preventDefault();
        selectRecurpayOption(label);
      });
    });
  }

  function paymentProductName(widget) {
    var start = widget;
    if (start && start.nodeType === 11 && start.host) start = start.host;
    var section = start && start.closest ? start.closest('[id^="shopify-section"], .product') || document : document;
    var title = section.querySelector('.product__title h1, .product__title h2');
    return title ? title.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function paymentControlName(control) {
    var labelledBy = control.getAttribute('aria-labelledby');
    if (labelledBy) {
      var fromIds = labelledBy.split(/\s+/).map(function (id) {
        var node = document.getElementById(id);
        return node ? node.textContent : '';
      }).join(' ').replace(/\s+/g, ' ').trim();
      if (fromIds) return fromIds;
    }

    var ariaLabel = (control.getAttribute('aria-label') || '').trim();
    if (ariaLabel) return ariaLabel;
    if (control.matches && control.matches('iframe')) return (control.getAttribute('title') || '').trim();
    return (control.innerText || control.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function labelPaymentControl(control, productName) {
    if (!control || !control.matches) return;
    if (control.matches('.shopify-payment-button__more-options, .shopify-payment-button__button--hidden')) return;
    if (!control.matches('button, a, iframe, input, [role="button"], [role="link"]')) return;

    var visibleText = (control.innerText || control.textContent || '').replace(/\s+/g, ' ').trim();
    var action = /paypal/i.test(visibleText)
      ? 'Pay with PayPal'
      : /shop/i.test(visibleText)
        ? 'Buy with Shop Pay'
        : 'Buy it now';
    var description = productName ? action + ', ' + productName : action;
    var name = paymentControlName(control);
    var generic = !name || name === 'Shopify embedded content' || name === 'Embedded content' || name === 'button';

    if (control.matches('iframe')) {
      if (generic) control.setAttribute('title', description);
      return;
    }

    if (!control.matches('button.gravity-button, button.shopify-payment-button__button, [role="button"].gravity-button')) return;
    if (control.getAttribute('aria-label') !== description) control.setAttribute('aria-label', description);
  }

  function eachPaymentControl(root, callback) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll('button, a, iframe, input, [role="button"], [role="link"]').forEach(callback);
    root.querySelectorAll('*').forEach(function (node) {
      if (node.shadowRoot) eachPaymentControl(node.shadowRoot, callback);
    });
  }

  function fixShopifyPaymentButtons(root) {
    var widgets = [];

    if (root.matches && root.matches('.shopify-payment-button')) widgets.push(root);
    if (root.querySelectorAll) {
      root.querySelectorAll('.shopify-payment-button').forEach(function (widget) {
        widgets.push(widget);
      });
    }

    widgets.forEach(function (widget) {
      var productName = paymentProductName(widget);
      eachPaymentControl(widget, function (control) {
        labelPaymentControl(control, productName);
      });
    });
  }

  function hideStyleContent(root) {
    if (!root) return;
    if (root.matches && root.matches('style, script')) {
      root.setAttribute('aria-hidden', 'true');
    }
    if (!root.querySelectorAll) return;
    root.querySelectorAll('style, script').forEach(function (node) {
      node.setAttribute('aria-hidden', 'true');
    });
  }

  function applyAccessibilityFixes(root) {
    hideStyleContent(root);
    fixRecurpayPurchaseOptions(root);
    fixShopifyPaymentButtons(root);
    fixNestedInteractive(root);
    fixProhibitedAria(root);
    removeEmptyHeadings(root);
    fixContentinfoLandmarks();
    fixCardHeadingLevels();
  }

  function scanAddedNode(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;

    var headingsChanged = false;

    /* Iframes */
    if (node.matches('iframe:not([title])')) {
      labelIframe(node);
    }

    node.querySelectorAll('iframe:not([title])').forEach(labelIframe);

    /* Judge.me verified badge */
    labelJudgeMeBadges(node);
    applyAccessibilityFixes(node);

    /* Klaviyo images */
    if (
      node.matches('img:not([alt])') &&
      node.closest(klaviyoSelector)
    ) {
      node.setAttribute('alt', 'Klaviyo promotional image');
    }

    node.querySelectorAll('img:not([alt])').forEach(function (image) {
      if (image.closest(klaviyoSelector)) {
        image.setAttribute('alt', 'Klaviyo promotional image');
      }
    });

    /* Wiser */
    if (node.matches('span.ws-sr-only[role="text"]')) {
      node.removeAttribute('role');
    }

    node
      .querySelectorAll('span.ws-sr-only[role="text"]')
      .forEach(function (element) {
        element.removeAttribute('role');
      });

    /* Headings */
    if (node.matches('h1') || node.querySelector('h1')) {
      headingsChanged = true;
    }

    return headingsChanged;
  }

  function scanInitialDocument() {
    /* Iframes */
    document
      .querySelectorAll('iframe:not([title])')
      .forEach(labelIframe);
    labelJudgeMeBadges(document);
    applyAccessibilityFixes(document);

    /* Klaviyo */
    document
      .querySelectorAll(klaviyoSelector + ' img:not([alt])')
      .forEach(function (image) {
        image.setAttribute('alt', 'Klaviyo promotional image');
      });

    /* Wiser */
    document
      .querySelectorAll('span.ws-sr-only[role="text"]')
      .forEach(function (element) {
        element.removeAttribute('role');
      });
  }

  function normalizeHeadings() {
    removeEmptyHeadings(document);

    var headings = Array.from(document.querySelectorAll('h1'));

    var meaningfulHeadings = headings.filter(function (heading) {
      return heading.textContent.trim() !== '';
    });

    /* Remove empty H1s */
    headings
      .filter(function (heading) {
        return (
          heading.textContent.trim() === '' &&
          !heading.classList.contains('header__heading')
        );
      })
      .forEach(function (heading) {
        heading.remove();
      });

    if (meaningfulHeadings.length < 2) return;

    var primaryHeading =
      meaningfulHeadings.find(function (heading) {
        return heading.matches(
          '.search-page-heading, .collection-hero__title, .product__title h1, .main-page-title, .article__title, .title--primary'
        );
      }) || meaningfulHeadings.find(function (heading) {
        return !heading.classList.contains('visually-hidden');
      }) || meaningfulHeadings[0];

    meaningfulHeadings.forEach(function (heading) {
      if (heading === primaryHeading) return;

      var replacement = document.createElement('h2');

      Array.from(heading.attributes).forEach(function (attribute) {
        replacement.setAttribute(
          attribute.name,
          attribute.value
        );
      });

      replacement.innerHTML = heading.innerHTML;

      heading.replaceWith(replacement);
    });
  }

  function scheduleHeadingNormalization() {
    if (headingNormalizationScheduled) return;

    headingNormalizationScheduled = true;

    requestAnimationFrame(function () {
      headingNormalizationScheduled = false;
      normalizeHeadings();
    });
  }

  scanInitialDocument();
  normalizeHeadings();
  setTimeout(fixContentinfoLandmarks, 1000);
  setTimeout(fixContentinfoLandmarks, 3000);
  setTimeout(function () { fixShopifyPaymentButtons(document); }, 1000);
  setTimeout(function () { fixShopifyPaymentButtons(document); }, 3000);

  new MutationObserver(function (records) {
    var headingsChanged = false;

    records.forEach(function (record) {
      Array.from(record.addedNodes).forEach(function (node) {
        if (scanAddedNode(node)) {
          headingsChanged = true;
        }
      });
    });

    if (headingsChanged) {
      scheduleHeadingNormalization();
    }
  }).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
